import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/Loader/Loader';
import { getVatRates } from '@/core/api';
import { formatDate } from '@/core/helpers';
import { normalizeDateFE } from '@/core/normalization';
import { useQuery } from '@tanstack/react-query';
import PageLayout from '@/wrappers/PageLayout';
import { Link, useNavigate } from 'react-router';
import Button from '@/components/Button';
import BackIcon from '@/icons/back.svg?react';
import CountryLabel from '@/components/CountryLabel/CountryLabel';
import { useMemo } from 'react';
import { Country, VatRate } from '@/core/types';
import { isBefore, sub } from 'date-fns';
import EU from '@/icons/eu-flag.svg?react';

const VAT_RATES_LINK =
  'https://europa.eu/youreurope/business/taxation/vat/vat-rules-rates/index_en.htm';

export interface GroupedData {
  country: Country;
  data: Partial<VatRate>[];
}

const excludedCountries = ['AX'];

export default function SellerVATIds() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['SellerVatRates'],
    queryFn: () => getVatRates(),
  });

  const groupedByCountry = useMemo(() => {
    const oneYearAgo = sub(new Date(), { years: 1 });
    return data?.reduce<GroupedData[]>((acc, item) => {
      const {
        country,
        vat_rate,
        vat_rate_option,
        effective_from,
        effective_till,
      } = item as VatRate;
      if (
        excludedCountries.includes(country.short_name) ||
        vat_rate_option.option_value === 'ZERO' ||
        isBefore(normalizeDateFE(item.effective_till), oneYearAgo)
      ) {
        return acc;
      }
      const existingCountry = acc.find(
        (d) =>
          d.country.id === country.id &&
          !excludedCountries.includes(d.country.short_name)
      );

      if (existingCountry) {
        existingCountry.data.push({
          vat_rate,
          vat_rate_option,
          effective_from,
          effective_till,
        });
      } else {
        acc.push({
          country,
          data: [{ vat_rate, vat_rate_option, effective_from, effective_till }],
        });
      }

      return acc;
    }, [] as GroupedData[]);
  }, [data]);

  if (isLoading) return <Loader />;

  return (
    <PageLayout
      className='flex-grow'
      rightColumn={
        <div style={{ width: 328 }} className='pt-4 lg:ml-auto lg:pt-0'>
          <div className='mb-4 ml-auto flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-white bg-white bg-opacity-50 px-11 py-6'>
            <EU />
            <div className='Title--sm text-center font-extrabold'>
              {t('Find additional information about VAT rates on EU website')}
            </div>
            <Link to={VAT_RATES_LINK} target='_blank' rel='noopener noreferrer'>
              <Button styling='outline' className='!px-2 !py-1 font-semibold'>
                <span>{t('Open')}</span>
              </Button>
            </Link>
          </div>
        </div>
      }
    >
      <div className='mx-auto max-w-[874px] rounded-lg bg-white py-3 pl-6 pr-4'>
        <div className='mb-4'>
          <Button
            styling='naked'
            onClick={() => {
              navigate(-1);
            }}
          >
            <BackIcon className='mr-2' />
            <span className='font-semibold'>{t('Back')}</span>
          </Button>
          <div className='my-2 text-h4 font-black'>
            {t('Information about VAT rates')}
          </div>
        </div>
        <div className='grid grid-cols-[1fr_2fr]'>
          <div className='text-gray-700'>{t('Country')}</div>
          <div className='text-gray-700'>{t('VAT rate')}</div>
        </div>
        {groupedByCountry?.map((group) => (
          <div
            key={group.country.id}
            className='grid grid-cols-[1fr_2fr] border-b py-2'
          >
            <div>
              <CountryLabel country={group.country.short_name || ''} />
            </div>
            <div>
              {group.data
                .sort((a, b) => {
                  const vatRateA = parseFloat(a.vat_rate || '0');
                  const vatRateB = parseFloat(b.vat_rate || '0');
                  return vatRateB - vatRateA;
                })
                .map((item, i) => (
                  <div key={group.country.id + i}>
                    {parseFloat(item.vat_rate || '0').toString()}%{' '}
                    <span>
                      {item.vat_rate_option?.option_value === 'HIGH'
                        ? t('standard rate')
                        : t('reduced rate')}
                    </span>{' '}
                    {!!item.effective_till && (
                      <span>{`(${t('effective till')} ${formatDate(
                        normalizeDateFE(item.effective_till),
                        'dd.MM.yy'
                      )})`}</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </PageLayout>
  );
}
